import { WarningIcon } from "@phosphor-icons/react";
import { isAxiosError } from "axios";
import { useIntl, type IntlShape } from "react-intl";

import { ReservationType } from "@/features/bookings/types/types";
import { type ApiError } from "@/types/types";

import PageError, { type PageErrorProps } from "./PageError";

interface Props {
  type: "flights" | "hotels";
  error: unknown;
}

const getErrorPageContent = ({
  type,
  error,
  intl,
}: Props & { intl: IntlShape }) => {
  let title =
    type === "flights"
      ? intl.formatMessage({
          defaultMessage: "Error while booking the ticket",
          description:
            "Error heading shown when the flight booking request fails",
        })
      : intl.formatMessage({
          defaultMessage: "An error occurred while booking the hotel",
          description:
            "Error heading shown when the hotel booking request fails",
        });
  let subtitles =
    type === "flights"
      ? [
          intl.formatMessage({
            defaultMessage:
              "Error while booking the ticket, please contact support team or try again later",
            description:
              "Error description when flight booking fails, with support contact instructions",
          }),
        ]
      : [
          intl.formatMessage({
            defaultMessage:
              "An error occurred while booking your hotel, please contact our support team or try again later",
            description:
              "Error description when hotel booking fails, with support contact instructions",
          }),
        ];
  let back: PageErrorProps["back"] = {
    text: intl.formatMessage({
      defaultMessage: "Back to home",
      description: "Button to return to the home / search screen",
    }),
    options: { to: type === "flights" ? "/flights/search" : "/hotels/search" },
  };

  if (!(error instanceof Error)) {
    title = intl.formatMessage({
      defaultMessage: "Payment couldn't be completed",
      description: "Heading shown when the payment attempt fails",
    });
    subtitles = [
      intl.formatMessage({
        defaultMessage:
          "Please check your QI account and try again the purchase later",
        description:
          "Description shown when payment fails, instructing user to check their QI account",
      }),
    ];
  }

  if (
    isAxiosError<ApiError>(error) &&
    error.response?.data.code === "VALIDATION_FAILED"
  ) {
    subtitles = [];
    back = {
      text: intl.formatMessage({
        defaultMessage: "Back",
        description: "Button to navigate back to the previous screen",
      }),
    };
    error.response?.data.data.forEach((validationError) => {
      subtitles.push(validationError.message);
    });
  }

  return { title, subtitles, back };
};

export const BookingError = ({ error, type }: Props) => {
  const intl = useIntl();

  const { title, subtitles, back } = getErrorPageContent({ error, type, intl });

  return (
    <PageError
      title={title}
      subtitles={subtitles}
      back={{ ...back }}
      link={{
        text: intl.formatMessage({
          defaultMessage: "Order details",
          description:
            "Section heading for order details in booking confirmations",
        }),
        options: {
          to: "/bookings",
          search: { category: type, reservationType: ReservationType.REFUNDED },
        },
      }}
      icon={<WarningIcon size={60} color="#de370c" />}
    />
  );
};
